/**
 * CriticalSpecialization (Foundry: rule-element/crit-spec.ts): the weapon group's critical
 * specialization effect on matching strikes, or an alternate effect with its own note, dice or
 * modifier. Applied to a strike's critical damage when the predicate passes for that strike.
 */
import { createDamageDice } from "../damage-dice";
import { localize } from "../labels";
import { createModifier } from "../modifiers";
import { testPredicate } from "../predicate";
import { cleanRuleValue, resolveInjectedProperties, resolveValue } from "../resolve";
import type { EngineState } from "../state";
import type { CritSpecEffect, CritSpecSynthetic } from "../synthetics";
import { DAMAGE_CATEGORIES_UNIQUE, isDamageType, isModifierType, type DamageDieSize } from "../values";
import type { EngineWeapon } from "../weapons";
import type { RuleHandler, RuleInstance } from "./base";

interface CritSpecData {
  alternate: boolean;
  text: string | null;
  damageDice: { number: unknown; faces: number; damageType: string | null; category: "persistent" | "precision" | "splash" | null } | null;
  modifier: { type: string; damageType: string | null; category: "persistent" | "precision" | "splash" | null; value: unknown } | null;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function parse(rule: RuleInstance): void {
  const s = rule.source;
  const alternate = s.alternate === true;
  const text = typeof s.text === "string" && s.text ? s.text : null;
  let damageDice: CritSpecData["damageDice"] = null;
  if (isPlainObject(s.damageDice)) {
    const d = s.damageDice;
    if (![4, 6, 8, 10, 12].includes(Number(d.faces))) return rule.fail("damageDice.faces: must be 4, 6, 8, 10 or 12");
    if (d.damageType !== undefined && d.damageType !== null && !isDamageType(d.damageType)) return rule.fail(`damageDice.damageType: "${String(d.damageType)}" is not a damage type`);
    if (d.category !== undefined && d.category !== null && !(DAMAGE_CATEGORIES_UNIQUE as readonly string[]).includes(String(d.category))) return rule.fail("damageDice.category: must be persistent, precision or splash");
    damageDice = { number: cleanRuleValue(d.number) ?? 1, faces: Number(d.faces), damageType: (d.damageType as string | null | undefined) ?? null, category: (d.category as CritSpecData["damageDice"] extends infer T ? (T extends { category: infer C } ? C : never) : never) ?? null };
  }
  let modifier: CritSpecData["modifier"] = null;
  if (isPlainObject(s.modifier)) {
    const m = s.modifier;
    if (m.type !== undefined && !isModifierType(m.type)) return rule.fail(`modifier.type: "${String(m.type)}" is not a modifier type`);
    if (m.damageType !== undefined && m.damageType !== null && !isDamageType(m.damageType)) return rule.fail(`modifier.damageType: "${String(m.damageType)}" is not a damage type`);
    modifier = { type: typeof m.type === "string" ? m.type : "untyped", damageType: (m.damageType as string | null | undefined) ?? null, category: (m.category as CritSpecData["modifier"] extends infer T ? (T extends { category: infer C } ? C : never) : never) ?? null, value: cleanRuleValue(m.value) ?? 0 };
  }
  if (alternate && !text && !damageDice && !modifier) return rule.fail("alternate: must also include at least one of text, damage dice, or modifier");
  if (!alternate && (text || damageDice || modifier)) return rule.fail(`${text ? "text" : damageDice ? "damageDice" : "modifier"}: may only be used if alternate is true`);
  rule.data = { alternate, text, damageDice, modifier } satisfies CritSpecData as unknown as Record<string, unknown>;
}

function getEffect(rule: RuleInstance, state: EngineState, weapon: EngineWeapon): CritSpecEffect {
  const data = rule.data as unknown as CritSpecData;
  const rawText = data.text ? resolveInjectedProperties(rule, state, data.text.trim(), { warn: false }) : null;
  const text = rawText ? (state.catalogue?.localize(rawText) ?? rawText) : null;
  const slug = "critical-specialization";
  const label = localize(state, "PF2E.Actor.Creature.CriticalSpecialization", "Critical Specialization");
  const groupKey = `PF2E.Item.Weapon.CriticalSpecialization.${weapon.group}`;
  const groupText = state.catalogue?.table("criticalSpecialization")?.[weapon.group ?? ""] ?? localize(state, groupKey, groupKey);
  const note = () =>
    data.alternate && !data.text
      ? null
      : { selector: "strike-damage", title: label, text: text ?? groupText, predicate: [], parentOptions: [], outcome: ["criticalSuccess" as const], visibility: null, source: rule.item.uuid, sourceName: rule.item.name };
  const resolveInteger = (value: unknown, fallback: number): number => {
    const resolved = resolveValue(rule, state, value, 0, { resolvables: { weapon }, warn: false });
    return Math.min(10, Math.max(1, Math.trunc(Math.abs(Number(resolved))))) || fallback;
  };
  const dice = () =>
    data.alternate && data.damageDice
      ? createDamageDice({ slug, label, selector: "strike-damage", diceNumber: resolveInteger(data.damageDice.number, 1), dieSize: `d${data.damageDice.faces}` as DamageDieSize, damageType: data.damageDice.damageType, category: data.damageDice.category, critical: true, source: rule.item.uuid, sourceName: rule.item.name })
      : null;
  const modifier = () =>
    data.alternate && data.modifier
      ? createModifier({ slug, label, type: data.modifier.type, value: resolveInteger(data.modifier.value, 0), damageType: data.modifier.damageType, damageCategory: data.modifier.category, critical: true, source: rule.item.uuid, sourceName: rule.item.name })
      : null;
  if (data.alternate) return [note(), dice(), modifier()].filter((e): e is NonNullable<typeof e> => !!e);
  switch (weapon.group) {
    case "crossbow":
    case "dart":
    case "knife": {
      const bleed = createDamageDice({ slug, selector: "strike-damage", label, damageType: "bleed", diceNumber: 1, dieSize: weapon.group === "crossbow" ? "d8" : "d6", critical: true, source: rule.item.uuid, sourceName: rule.item.name });
      const bonusValue = weapon.attackItemBonus;
      const bonus = bonusValue > 0 ? createModifier({ slug, label, type: "item", damageType: "bleed", value: bonusValue, critical: true, source: rule.item.uuid, sourceName: rule.item.name }) : null;
      return bonus ? [bleed, bonus] : [bleed];
    }
    case "pick":
    case "sniper":
      return weapon.baseDamage.die ? [createModifier({ slug, label, type: "untyped", value: 2 * weapon.baseDamage.dice, critical: true, source: rule.item.uuid, sourceName: rule.item.name })] : [];
    default: {
      const n = note();
      return weapon.group && n ? [n] : [];
    }
  }
}

function beforePrepareData(rule: RuleInstance, state: EngineState): void {
  if (rule.ignored) return;
  const data = rule.data as unknown as CritSpecData;
  const synthetic: CritSpecSynthetic = (weapon, options) => {
    const predicate = resolveInjectedProperties(rule, state, structuredClone(rule.predicate), { warn: false });
    return testPredicate(predicate, new Set([...options, ...rule.item.getRollOptions("parent")])) ? getEffect(rule, state, weapon) : null;
  };
  if (rule.ignored) return;
  state.synthetics.criticalSpecializations[data.alternate ? "alternate" : "standard"].push(synthetic);
}

export const CriticalSpecializationHandler: RuleHandler = { key: "CriticalSpecialization", parse, beforePrepareData };
